import { Users } from 'features/account/types'
import { User, AvatarData, UserRole } from 'features/users/types'

export type UserLike = User | Users

const rolePriority: { [key in UserRole]: number } = {
  Superadmin: 1,
  Admin: 2,
  Builder: 3,
  Viewer: 4,
  Guest: 5
}

export function getAvatarData(user?: Partial<UserLike>): AvatarData {
  const userImage = user?.picture
  const acronym = user?.name?.slice(0, 1).toUpperCase()
  const email = user?.email || user?.name

  return { userImage, acronym, email }
}
/**
 * @deprecated Use `getRoleInfo` instead
 * @param user
 * @returns
 */
export function getRole(user?: Partial<UserLike>): UserRole {
  // the roles might not be in order and we need to get the higher level
  const roles = user?.app_metadata?.roles
  if (!roles || roles.length === 0) {
    return UserRole.Guest
  }

  const sorted = [...roles].sort((a, b) => rolePriority[a] - rolePriority[b])

  return sorted[0]
}

export function getRolePriority(role: UserRole): number {
  return rolePriority[role]
}

export function getRoleInfo(user?: Partial<UserLike>): { role: UserRole; priority: number } {
  const roles = user?.app_metadata?.roles || []
  return roles.reduce<{ role: UserRole; priority: number }>(
    (acc, current) => {
      let role = acc.role
      if (getRolePriority(current) < acc.priority) {
        role = current
      }
      return { role, priority: getRolePriority(role) }
    },
    { role: UserRole.Guest, priority: rolePriority[UserRole.Guest] }
  )
}

// These utility functions are recommended for a single use on an arbitrary user, selected by consumer.
// If several of these checks are required and all of them are related to *current user*, then it is
// recommended to use instead the `useUserRoles` hook.
export function isSuperadmin(user?: Partial<UserLike>) {
  return user?.app_metadata?.roles?.includes(UserRole.Superadmin) || false
}

export function isAdmin(user?: Partial<UserLike>) {
  return user?.app_metadata?.roles?.includes(UserRole.Admin) || false
}

export function isEditor(user?: Partial<UserLike>) {
  return (!isAdmin(user) && user?.app_metadata?.roles?.includes(UserRole.Builder)) || false
}

export function isViewer(user?: Partial<UserLike>) {
  return (!isAdmin(user) && !isEditor(user) && user?.app_metadata?.roles?.includes(UserRole.Viewer)) || false
}

export function hasEditionRole(user?: Partial<UserLike>) {
  return isAdmin(user) || isEditor(user)
}

export function isGuest(user?: Partial<UserLike>) {
  return user?.app_metadata?.roles?.includes(UserRole.Guest) || false
}

export function getAuthMethodDescription(user: Partial<UserLike>) {
  if (!user?.identities?.length) {
    return 'Unknown'
  }

  const identity = user.identities[0]

  // Alias for known connection names
  const aliasForKnownConnectionNames = {
    'Username-Password-Authentication': 'User/Password',
    'google-oauth2': 'Google Account',
    github: 'GitHub'
    // SSO connections are not listed here, as names are custom
  }

  return aliasForKnownConnectionNames[identity.connection] || 'SSO'
}

export function getAuthMethodDescriptionByUserId(userId: string) {
  const [authMethod] = userId.split('|', 2)

  const authMethods: { [key: string]: string } = {
    auth0: 'User/Password',
    'google-oauth2': 'Google Account',
    github: 'Github'
  }

  return authMethods[authMethod] || 'SSO'
}

export function getConnectionNameByUserId(user: Partial<UserLike>) {
  if (!user?.identities?.length) {
    return 'Unknown'
  }

  const identity = user.identities[0]
  return identity.connection
}

export function getDuplicateUsersByEmailAndProvider(users: Partial<Users>[]) {
  return users.reduce((duplicates: string[], user) => {
    const sameEmailAndProvider = users.filter(
      (u) =>
        u.email &&
        user.email &&
        u.email === user.email &&
        u.identities?.[0]?.provider === user.identities?.[0]?.provider
    )

    if (sameEmailAndProvider.length > 1 && user.email && !duplicates.includes(user.email)) {
      duplicates.push(user.email)
    }

    return duplicates
  }, [])
}
